import { LoadingButton } from "@mui/lab";
import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useGlobalContext } from "../context/GlobalContext";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { useGlobalAuthContext } from "../context/AuthContext";
import { ReactComponent as UserIcon } from "../Assets/User-Black.svg";
import { ReactComponent as DeleteIcon } from "../Assets/delete.svg";
import { ReactComponent as ShareWhiteIcon } from "../Assets/Share-White.svg";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import {
  useDeleteSavedLayoutMutation,
  useGetSavedLayoutSpecficUserQuery,
  useChangePswMutation,
  useGetLayoutVersionsByIDQuery,
} from "../Slices/roomNateApis";
import { useUserProfileSelector } from "../hooks/useUserProfile";
import {
  mfLogGuestGalleryAuth,
  mfGalleryDebugEnabled,
} from "../utils/galleryStaleTransitionDebug";

import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DynamicAlert, SuccessAlert } from "../utils/Alerts";
import { truncateText } from "../utils/truncateText";
import axios from "axios";
import { ReactComponent as ExitIcon } from "../Assets/exit.svg";
import * as pdfjsLib from "pdfjs-dist";

import { ReactComponent as LogoBgIcon } from "../Assets/mf-logo.svg";
import { baseUrl } from "../utils/baseUrl";
import NewFolderDialog from "../Components/NewFolder";
import MoveFolderDilogue from "../Components/MoveFolder";
import DeleteDialog from "../Components/DeleteDilogue";
import DragAndDropFile from "../Components/Account/drag-and-drop";
import UpdateProfile from "./UpdatePage";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UploadFloor from "./UploadFloor";
import NewUpload from "../Components/NewUpload";
import { apiRoomNate } from "../Slices/roomNateApis";
import LayoutOptionMenu from "../Components/LayoutOptionMenu";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LayoutVersionSlider from "../Components/LayoutVersionSlider";
import ErrorBoundary from "../Components/ErrorBoundary";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import LoaderSpinner from "../Components/LoaderSpinner";
import MainBentoCombinedLibraryBody from "../Components/UserHome/MainBentoCombinedLibraryBody";
import StandardDialog, { mfModalPillButtonSx } from "../Components/StandardDialog";
import posthog from "posthog-js";
import { getBase64Image } from "../utils/covertor";
import layoutCacheStore from "../utils/Cache";
import { useAnalytics } from '../utils/eventLogAnalytics';
import { useSearchParams } from 'react-router-dom';
import PropTypes from "prop-types";
import { normalizeTextBoxes } from "../utils/normalizeTextBoxes";

/** Flatten all saved layouts with images from a single tree node (same semantics as in-component `filterLayouts`). */
function filterLayoutsFromNode(data) {
  let result = [];
  if (data.image) {
    result.push(data);
  }
  if (data.children) {
    data.children.forEach((child) => {
      result = result.concat(filterLayoutsFromNode(child));
    });
  }
  return result;
}

function findLayoutNodeById(nodes, id) {
  if (id == null || !nodes?.length) return null;
  for (const node of nodes) {
    if (node._id === id) return node;
    if (node.children?.length) {
      const found = findLayoutNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

const SketchUpload = ({
  mainHomeEmbedded = false,
  dashboardShell = null,
  /** `/main` bento: fill parent flex height; scroll inside library only (no 100dvh min-heights). */
  mainHomeBentoFill = false,
  /** When set (e.g. from `UserMainHome`), filters the bento floor-plan grid to that folder; `null` = all plans. */
  selectedFolderId: selectedFolderIdProp = undefined,
  setSelectedFolderId: setSelectedFolderIdProp = undefined,
  /** Lifted from `UserMainHome` so “+ New” in the sidebar opens the same dialog as the rest of the page. */
  showNewFolder: showNewFolderProp = undefined,
  setShowNewFolder: setShowNewFolderProp = undefined,
  /** Desktop drag-and-drop from `UserMainHome` / bento grid. */
  bentoDraggingPlanId = null,
  onBentoPlanDragStart = undefined,
  onBentoPlanDragEnd = undefined,
  /**
   * Optional ref populated with imperative handlers so `UserMainHome` can
   * open the same `LayoutOptionMenu` dialog from the mobile floor-plan
   * slider without lifting `editableLayoutId` / `optionsMenu` state.
   */
  externalOptionsMenuHandle = undefined,
}) => {
  const { currentUserSessionStorage, Logout } = useGlobalAuthContext();

  const {
    setUploadImage,
    uploadImage,
    setModalData,
    setModalVertices,
    setInitialVertices,
    setPlanLabelBoxes,
    setFloors,
    stackedLayouts,
    setStackedLayouts,
    activeLayout,
    setActiveLayout,
    editVersionLayout,
    setEditVersionLayout,
    showVersionLayout,
    setShowVersionLayout,
    value,
    setValue,
    forkTransitioning,
    doneEditing,
    setDoneEditing,
    setClientRef,
  } = useGlobalContext();

  /** Hide duplicate header on /main bento (profile lives in UserMainHome). */
  const bentoHomeChromeHidden =
    mainHomeEmbedded &&
    dashboardShell === "bento" &&
    (value === "1" || value === "2");

  const libraryFillsParentHeight = Boolean(mainHomeBentoFill) && bentoHomeChromeHidden;

  /** `/main` bento: single scrollable column — folders first, floor plans below (no tab switcher). */
  const combineMainBentoLibrary =
    mainHomeEmbedded &&
    dashboardShell === "bento" &&
    (value === "1" || value === "2");

  // Navigation and dispatch
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // RTK Query hooks — skip for guests (no auth token)
  const EMPTY_LAYOUTS = []; // prevent needless re-render if no layout
  const isGuest = !currentUserSessionStorage;
  const {
    data: layoutDataRtk = EMPTY_LAYOUTS,
    isFetching: layoutsFetching,
    refetch: refetchLayoutsData,
  } = useGetSavedLayoutSpecficUserQuery(undefined, { skip: isGuest });
  const [changePsw, resultStats] = useChangePswMutation();

  const [showNewFolderInternal, setShowNewFolderInternal] = useState(false);
  const showNewFolder =
    showNewFolderProp !== undefined ? showNewFolderProp : showNewFolderInternal;
  const setShowNewFolder =
    setShowNewFolderProp !== undefined
      ? setShowNewFolderProp
      : setShowNewFolderInternal;

  // Local state
  const [editableLayoutId, setEditableLayoutId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [payloadData, setPayloadData] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [title, setTitle] = useState("");
  const [load, setLoad] = useState(false);
  const [alertStatus, setAlertStatus] = useState(null);

  // Refs
  const controllerRef = useRef(null);
  const timeoutRef = useRef(null);
  const progressUpdaterRef = useRef(null);
  const randomProgressUpdateRef = useRef(null);
  const errorProcessImageRef = useRef(null);
  const projectImageRef = useRef(null);
  const fileInputRef = useRef(null);
  const editableLayoutIdRef = useRef(null);

  const [searchParams] = useSearchParams();
  const { eventLog } = useAnalytics(searchParams.get('clientRef') || 'custom-ref-not-provided');

  // Keep editableLayoutIdRef in sync
  useEffect(() => {
    editableLayoutIdRef.current = editableLayoutId;
    if (params?.galleryId) {
      setShowVersionLayout(params?.galleryId);
    }
    // 2. Listen for the incoming ref, if supplied via window message
    const handleIncomingData = (event) => {
      if (event.data.type === 'mf:set_ref') {
        const { ref } = event.data.payload;
        console.log("Received clientRef:", ref);
        setClientRef(ref);
      }
    };
    window.addEventListener('message', handleIncomingData);
    return () => window.removeEventListener('message', handleIncomingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableLayoutId]);

  const filterDirectory = useCallback(function filterDirectory(data) {
    let result = [];
    if (!data.image) {
      result.push(data);
    }
    if (data.children) {
      data.children.forEach((child) => {
        result = result.concat(filterDirectory(child));
      });
    }
    return result;
  }, []);

  const layoutDataRef = useRef(EMPTY_LAYOUTS);
  if (
    layoutDataRtk !== undefined &&
    (layoutDataRtk.length !== layoutDataRef.current.length ||
     layoutDataRtk.some((item, i) => item !== layoutDataRef.current[i]))
  ) {
    layoutDataRef.current = layoutDataRtk;
  }
  const layoutData = layoutDataRef.current;

  const allPlansOrdered = useMemo(() => {
    if (!layoutDataRtk?.length) return [];
    return layoutDataRtk
      .flatMap(filterLayoutsFromNode)
      .filter((item) => item.image && item?.userSavedLayoutsCount > 0)
      .slice()
      .reverse();
  }, [layoutDataRtk]);

  const bentoFloorPlanItems = useMemo(() => {
    if (!combineMainBentoLibrary) return null;
    if (selectedFolderIdProp == null) return allPlansOrdered;
    const folder = findLayoutNodeById(layoutDataRtk, selectedFolderIdProp);
    if (!folder) return allPlansOrdered;
    return filterLayoutsFromNode(folder)
      .filter((item) => item.image && item?.userSavedLayoutsCount > 0)
      .slice()
      .reverse();
  }, [
    combineMainBentoLibrary,
    layoutDataRtk,
    selectedFolderIdProp,
    allPlansOrdered,
  ]);

  const bentoFloorPlanSectionTitle = useMemo(() => {
    if (!combineMainBentoLibrary) return "Floor plans";
    const count = bentoFloorPlanItems?.length ?? 0;
    if (selectedFolderIdProp == null) {
      return count > 0 ? `All plans (${count})` : "All plans";
    }
    const folder = findLayoutNodeById(layoutDataRtk, selectedFolderIdProp);
    const name = (folder?.name && String(folder.name).trim()) || "Untitled";
    return count > 0 ? `${name} (${count})` : name;
  }, [
    combineMainBentoLibrary,
    bentoFloorPlanItems,
    selectedFolderIdProp,
    layoutDataRtk,
  ]);

  useEffect(() => {
    const updatedMenu = [
      ...(layoutData
        ?.flatMap(filterDirectory)
        ?.filter((dir) => !dir.payload && editableLayoutId?._id !== dir?._id)
        ?.sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" })
        )
        ?.map((dir) => ({
          icon: <FolderOutlinedIcon fontSize="small" />,
          label: dir?.name || "Untitled",
          value: async () => {
            const layoutId = editableLayoutIdRef?.current?._id;
            await axios.put(
              `${baseUrl}/savedLayout/edit/${layoutId}`,
              { parent: dir?._id },
              {
                headers: {
                  Authorization: `Bearer ${currentUserSessionStorage}`,
                },
              }
            );
            setOptionsMenu(null);
            setEditableLayoutId(null);
            if (value == "2") refetchLayoutsData();
            else setReloadDirectories(true);
          },
        })) || []),
      {
        icon: <AddCircleOutlinedIcon fontSize="small" color="white" />,
        label: "Create folder",
        value: async () => {
          setShowNewFolder(true);
        },
      },
    ];
    setMoveMenu(updatedMenu);
  }, [
    layoutData,
    editableLayoutId,
    currentUserSessionStorage,
    value,
    refetchLayoutsData,
    filterDirectory,
    setShowNewFolder,
  ]);

  const formik = useFormik({
    initialValues: {
      password: "",
      oldPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .required("Old password is required.")
        .min(8, "Password is too short - should be 8 chars minimum."),
      password: Yup.string()
        .required("New password is required.")
        .min(8, "Password is too short - should be 8 chars minimum."),
      confirmPassword: Yup.string()
        .required("Confirm your new password.")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      changePsw({
        body: {
          currentPassword: values?.oldPassword,
          newPassword: values?.password,
        },
      });
    },
  });

  /** 16px input text avoids iOS zoom on focus; matches modal field styling. */
  const changePasswordFieldSx = {
    "& .MuiInputBase-input": { fontSize: "16px" },
    "& .MuiOutlinedInput-root": { borderRadius: "12px", overflow: "visible" },
  };

  useEffect(() => {
    projectImageRef.current = projectDetails?.image;
  }, [projectDetails?.image]);

  useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }
    if (resultStats?.isSuccess) {
      setLoad(false);
      setTitle(resultStats?.data?.message);
      setAlertStatus(true);
      setValue("3");
    } else if (resultStats?.isError) {
      setLoad(false);
      setTitle(resultStats?.error?.data?.error || resultStats?.error?.data);
      setAlertStatus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultStats, setValue]);

  const [successAlert, setSuccessAlert] = useState("");

  const [reloadDirectories, setReloadDirectories] = useState(false);
  const [deleteLayout] = useDeleteSavedLayoutMutation();


  const location = useLocation();
  const params = useParams();
  const redirectToEditor = location.search?.includes('redirect-to-editor');

  const [showPsw, setShowPsw] = React.useState(false);

  const { userProfile: profile } = useUserProfileSelector();
 
  // const [editableLayoutId, setEditableLayoutId] = useState(null);

  const [layouts, setLayouts] = useState([]);
  const [showLayouts, setShowLayouts] = useState([]);
  const [parentLayout, setParentLayout] = useState([null]);
  const [showMoveFolder, setShowMoveFolder] = useState(false);
  const [showCopyFolder, setShowCopyFolder] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [optionsMenu, setOptionsMenu] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);

  const versionSliderData = useGetLayoutVersionsByIDQuery(showVersionLayout || params?.id, {
    skip: !showVersionLayout && !params?.id,
    refetchOnMountOrArgChange: true,
  });
  useEffect(() => {
    if (!layoutsFetching) {
      setLayouts(layoutDataRef.current);
    }
    if (mfGalleryDebugEnabled()) {
      console.log("EVAL: SHOULD REDIRECT? ", redirectToEditor, versionSliderData);
    }
    if (redirectToEditor && versionSliderData.isSuccess) {
      const layout = versionSliderData.data;
      const editorPath = isGuest ? `/floor-gallery/${layout?._id}` : `/floor-plan/${layout?.uuid}/version/${layout?.layoutsVersions[0]?.version}`;
      setEditVersionLayout(layout?.layoutsVersions[0]?.version);
      setActiveLayout(layout);
      if (isGuest) {
        mfLogGuestGalleryAuth("sketchUpload.redirectToEditorGuest", {
          layoutId: layout?._id,
          layoutUuid: layout?.uuid ?? null,
          targetPath: editorPath,
          version: layout?.layoutsVersions?.[0]?.version ?? null,
        });
      }
      console.log("NAVIGATE TO: ", editorPath);
      navigate(editorPath, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutsFetching, versionSliderData.isSuccess, isGuest]); // layoutData intentionally excluded

  const restoreStack = useCallback((lastParentId) => {
    const newShowLayouts = [layoutData];
    let pos = layoutData;
    parentLayout.forEach((layoutId) => {
      if (!layoutId) return;
      const parent = pos.find((dir) => dir._id === layoutId);
      if (parent) {
        newShowLayouts.push(parent?.children || []);
        pos = parent?.children || [];
      }
    });
    setShowLayouts(newShowLayouts);
  }, [layoutData, parentLayout, setShowLayouts]);

  useEffect(() => {
    if (combineMainBentoLibrary) return;
    if (layoutData && layoutData.length > 0) {
      if (parentLayout.length > 1) {
        const lastParentId = parentLayout[parentLayout.length - 1];
        restoreStack(lastParentId);
      } else {
        setShowLayouts([layoutData]);
      }
    }
  }, [combineMainBentoLibrary, layoutData, parentLayout, restoreStack]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setEditableLayoutId(null);
  };

  function calculateNumericInches(valueString) {
    if (!valueString) return 0; 

    const dimensions = valueString.split(",").map((dim) => dim.trim());
    let totalInches = 0;

    dimensions.forEach((dim) => {
      const match = dim.match(/(\d+)'?\s*(\d+)?"?/);

      if (match) {
        const feet = match[1] ? parseInt(match[1], 10) : 0; 
        const inches = match[2] ? parseInt(match[2], 10) : 0; 

        totalInches += feet * 12 + inches;
      }
    });

    return totalInches; 
  }

  function processWalls(walls) {
    return walls.map((wall) => ({
      start: {
        x: parseInt(wall.start.x, 10),
        y: parseInt(wall.start.y, 10),
      },
      end: {
        x: parseInt(wall.end.x, 10),
        y: parseInt(wall.end.y, 10),
      },
    }));
  }

  function processRooms(data) {
    return data.rooms.map((room) => {
      const updatedRoom = { ...room };

      if (room.height) {
        updatedRoom.height = calculateNumericInches(room.height || 0);
      }

      if (room.width) {
        updatedRoom.width = calculateNumericInches(room.width || 0); 
      }

      if (room.walls) {
        updatedRoom.walls = processWalls(room.walls);
      }

      return updatedRoom;
    });
  }

  function convertWallsToNewFormat(rooms) {
    let modifiedWalls = [];
    rooms.forEach((room) => {
      room.walls.forEach((wall) => {
        modifiedWalls.push([
          { x: parseInt(wall.start.x), y: parseInt(wall.start.y) },
          { x: parseInt(wall.end.x), y: parseInt(wall.end.y) },
        ]);
      });
    });
    return modifiedWalls;
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (file.type === "application/pdf") {
      const reader = new FileReader();

      reader.onload = async function () {
        const arrayBuffer = reader.result;
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const totalPages = pdf.numPages; 

        const pagePromises = [];

        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
          const pagePromise = pdf.getPage(pageNumber).then(async (page) => {
            const viewport = page.getViewport({ scale: 2 }); 

            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            canvas.height = viewport.height; 
            canvas.width = viewport.width; 

            await page.render({ canvasContext: context, viewport }).promise;

            return { canvas, viewport, pageNumber }; 
          });

          pagePromises.push(pagePromise);
        }

        const pageDetails = await Promise.all(pagePromises);

        const totalHeight = pageDetails.reduce(
          (total, { viewport }) => total + viewport.height,
          0
        );
        const maxWidth = Math.max(
          ...pageDetails.map(({ viewport }) => viewport.width)
        ); 

        const finalCanvas = document.createElement("canvas");
        const finalContext = finalCanvas.getContext("2d");
        finalCanvas.height = totalHeight; 
        finalCanvas.width = maxWidth; 

        let currentY = 0; 

        pageDetails.forEach(({ canvas, viewport }) => {
          finalContext.drawImage(canvas, 0, currentY); 
          currentY += viewport.height; 
        });

        finalCanvas.toBlob((blob) => {
          const pngFile = new File([blob], "combined_pages.png", {
            type: "image/png",
          });

          setUploadImage(pngFile);
          setValue("6");

          // Delay processImage to ensure state updates are applied
          setTimeout(() => {
            processImage(pngFile, finalCanvas.width, finalCanvas.height);
          }, 0);
        }, "image/png");
      };

      reader.readAsArrayBuffer(file);
    } else {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = function () {
        setUploadImage(file);
        setValue("6");

        // Delay processImage to ensure state updates are applied
        setTimeout(() => {
          processImage(file, img.width, img.height);
        }, 0);
      };
    }

    event.target.value = null;
  };

  const handleCancelRequest = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null; 
    }
  };

  const processImage = async (file, imgWidth, imgHeight) => {
    if (controllerRef.current) {
      controllerRef.current.abort(); 
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (progressUpdaterRef.current) {
      clearTimeout(progressUpdaterRef.current);
    }
    if (randomProgressUpdateRef.current) {
      clearInterval(randomProgressUpdateRef.current);
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("response_type", "json");

    setLoading(true);
    setProgress(0);
    errorProcessImageRef.current = null;

    controllerRef.current = new AbortController();

    let progressUpdater;
    let progressStopped = false; 

    const startRandomProgress = () => {
      const updateProgress = () => {
        setProgress((prev) => {
          if (prev >= 99 || progressStopped) return prev; 
          const randomJump = Math.floor(Math.random() * 10) + 1; 
          return Math.min(prev + randomJump, 99); 
        });

        if (!progressStopped) {
          const randomInterval = Math.floor(Math.random() * 700) + 300; 
          progressUpdater = setTimeout(updateProgress, randomInterval);
        }
      };

      updateProgress();
    };

    startRandomProgress();

    timeoutRef.current = setTimeout(() => {
      progressStopped = true;
      setUploadImage(null);
      errorProcessImageRef.current = true; 
      console.error("Timeout: Process image request took too long.");
      controllerRef.current.abort();
      clearTimeout(progressUpdater); 
      setLoading(false);
    }, 100000); 

    try {
      const response = await axios.post(
        "https://ai.magicfurnish.com/process-image",
        formData,
        { signal: controllerRef.current.signal }
      );

      progressStopped = true;
      clearTimeout(progressUpdater);

      const finalizeProgress = setInterval(() => {
        setProgress((prev) => {
          // Always allow progress to reach 100 when AI processing completes successfully
          const maxProgress = 100; 

          if (prev >= maxProgress) {
            clearInterval(finalizeProgress);
            return maxProgress;
          }

          const randomJump = Math.floor(Math.random() * 5) + 1; 
          return Math.min(prev + randomJump, maxProgress);
        });
      }, Math.floor(Math.random() * 500) + 200); 

      const { data } = response;
      setModalData(processRooms(data));
      setPlanLabelBoxes(normalizeTextBoxes(data?.text_boxes));
      setModalVertices(convertWallsToNewFormat(data?.rooms));
      setInitialVertices(convertWallsToNewFormat(data?.rooms));
      setFloors(calculateBounds(data?.rooms, imgWidth, imgHeight));

      setPayloadData({
        modalData: processRooms(data),
        boxes: [],
        planLabelBoxes: normalizeTextBoxes(data?.text_boxes),
        modalVertices: convertWallsToNewFormat(data?.rooms),
        floors: calculateBounds(data?.rooms, imgWidth, imgHeight),
      });
    } catch (error) {
      progressStopped = true; 
      clearTimeout(progressUpdater);
      console.error("Error processing the image:", error);

      if (axios.isCancel(error)) {
      } else {
        errorProcessImageRef.current = true;
        setUploadImage(null);
      }
    } finally {
      clearTimeout(progressUpdater);
      clearTimeout(timeoutRef.current);
      setLoading(false);
    }
  };


  function calculateBounds(data, imgWidth, imgHeight) {
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    data.forEach((room) => {
      room.walls.forEach((wall) => {
        const startX = parseFloat(wall.start.x);
        const startY = parseFloat(wall.start.y);
        const endX = parseFloat(wall.end.x);
        const endY = parseFloat(wall.end.y);

        minX = Math.min(minX, startX, endX);
        maxX = Math.max(maxX, startX, endX);
        minY = Math.min(minY, startY, endY);
        maxY = Math.max(maxY, startY, endY);
      });
    });

    return {
      extraLeft: minX,
      extraRight: imgWidth - maxX,
      extraTop: minY,
      extraBottom: imgHeight - maxY,
    };
  }

  const resetProgress = () => {
    setProgress(0);
  };

  const LogoutMain = () => {
    Logout();

    handleCancelRequest();
    setUploadImage(null);
    resetProgress();

    setLayouts([]);
    setStackedLayouts([]);
    setActiveLayout(null);
    dispatch(apiRoomNate.util.resetApiState());
    posthog.reset();
  };

  useEffect(() => {
    if (value == "2" && reloadDirectories) {
      refetchLayoutsData();
      setReloadDirectories(false);
    }
  }, [value, reloadDirectories, refetchLayoutsData]);

  const [moreOptionsMenu, setMoreOptionsMenu] = useState([]);
  const [moveMenu, setMoveMenu] = useState([]);

  // ...existing code...

  useEffect(() => {
    const updatedMenu = [
      ...(layoutData
        ?.flatMap(filterDirectory)
        ?.filter((dir) => !dir.payload && editableLayoutId?._id !== dir?._id)
        ?.sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" })
        )
        ?.map((dir) => ({
          icon: <FolderOutlinedIcon fontSize="small" />,
          label: dir?.name || "Untitled",
          value: async () => {
            const layoutId = editableLayoutIdRef?.current?._id;
            await axios.put(
              `${baseUrl}/savedLayout/edit/${layoutId}`,
              { parent: dir?._id },
              {
                headers: {
                  Authorization: `Bearer ${currentUserSessionStorage}`,
                },
              }
            );
            setOptionsMenu(null);
            setEditableLayoutId(null);
            if (value == "2") refetchLayoutsData();
            else setReloadDirectories(true);
          },
        })) || []),
      {
        icon: <AddCircleOutlinedIcon fontSize="small" color="white" />,
        label: "Create folder",
        value: async () => {
          setShowNewFolder(true);
        },
      },
    ];

    setMoveMenu(updatedMenu);
  }, [
    layoutData,
    editableLayoutId,
    currentUserSessionStorage,
    value,
    refetchLayoutsData,
    filterDirectory,
    setShowNewFolder,
  ]);

  useEffect(() => {
    setMoreOptionsMenu([
      { label: "Share", share: true },
      {
        label: "Move to folder",
        value: () => setOptionsMenu(moveMenu),
      },
      {
        label: "Delete",
        value: () => {
          setItemToDelete(editableLayoutIdRef?.current);
          setOptionsMenu(null);
        },
        danger: true,
      },
    ]);
  }, [moveMenu]);

  // Expose an imperative handle so the mobile floor-plan slider in
  // `UserMainHome` (which lives outside this subtree's Paper) can open the
  // same options dialog without us having to lift `editableLayoutId` and
  // `optionsMenu` state up the tree.
  useEffect(() => {
    if (!externalOptionsMenuHandle) return undefined;
    externalOptionsMenuHandle.current = {
      openFloorPlanCard: (item) => {
        if (!item) return;
        if (clickTimeout) clearTimeout(clickTimeout);
        const timeout = setTimeout(() => {
          if (
            item?.version == 2 &&
            item?.payload?.roomsDataCount == 0
          ) {
            setActiveLayout(item);
            setEditVersionLayout(2);
            navigate(`/floor-plan/${item?.uuid}/version/${2}`);
          } else {
            setShowVersionLayout(item?._id);
            navigate(`/floor-gallery/${item?._id}`);
          }
        }, 200);
        setClickTimeout(timeout);
      },
      openFloorPlanCardDoubleClick: (item) => {
        if (clickTimeout) clearTimeout(clickTimeout);
        setActiveLayout({
          name: item?.name,
          uuid: item?.uuid,
        });
      },
      openOptionsMenu: (item) => {
        if (!item) return;
        setEditableLayoutId(item);
        setOptionsMenu(moreOptionsMenu);
      },
    };
    return () => {
      if (externalOptionsMenuHandle.current) {
        externalOptionsMenuHandle.current = null;
      }
    };
  }, [
    clickTimeout,
    externalOptionsMenuHandle,
    moreOptionsMenu,
    navigate,
    setActiveLayout,
    setEditVersionLayout,
    setShowVersionLayout,
  ]);

//
// DEBuGGINg HELPER used to track why [Done Editing] button required 2 clicks to transfer to Gallery view
//
//const useEffectDebugger = (effectFn, deps, depNames) => {
//  const prevDeps = useRef([]);
//
//  useEffect(() => {
//    const changed = deps.reduce((acc, dep, i) => {
//      if (dep !== prevDeps.current[i]) {
//        acc.push({
//          name: depNames?.[i] ?? `dep[${i}]`,
//          prev: prevDeps.current[i],
//          next: dep,
//        });
//      }
//      return acc;
//    }, []);
//
//    if (changed.length > 0) {
//      console.group('[useEffectDebugger] effect triggered by:');
//      changed.forEach(({ name, prev, next }) => {
//        console.log(`  ${name}:`, prev, '→', next);
//      });
//      console.groupEnd();
//    }
//
//    prevDeps.current = deps;
//    return effectFn();
//  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
//}
//
// // use the 'useEffectDebugger' as drop-in replacement for 'useEffect'
//useEffectDebugger(
//  () => {
//    if (params?.layoutId && params?.version && !activeLayout) {
//      if (doneEditing) {
//        if (activeLayout) {
//          setActiveLayout(null);
//        }
//        setDoneEditing(false);
//      } else {
//        setActiveLayout({ uuid: params?.layoutId });
//      }
//      setEditVersionLayout(params?.version);
//      setShowVersionLayout(null);
//    }
//  },
//  [params?.layoutId, params?.version, activeLayout, setActiveLayout, setEditVersionLayout, setShowVersionLayout],
//  ['layoutId',       'version',        'activeLayout', 'setActiveLayout', 'setEditVersionLayout', 'setShowVersionLayout']
//);

  useEffect(() => {
    if (params?.galleryId && !doneEditing) {
      // `origin` tags how the visitor arrived at the gallery. Today every
      // mount fires once with origin: 'link' (page load from a shared URL
      // or in-app navigation). Future origins (e.g. 'editor_return' when
      // we explicitly capture returns from the editor) can be added without
      // breaking older events — anything without an `origin` field is
      // implicitly treated as a fresh load by downstream analytics.
      eventLog(window.opener, 'floor_plan_event', 'webview_opened', {
          galleryId: params.galleryId,
          galleryUrl: params.galleryId ? `${window.location.origin}/floor-gallery/${params.galleryId}` : undefined,
          origin: 'link',
      });
      setDoneEditing(true);
    }
    if (params?.layoutId && params?.version && !activeLayout) {
      // [Done Editing] button required 2 clicks to transfer back to Gallery View.
      // Root cause: this effect rewrote the null value in activeLayout, needed for transition to Gallery, with layoutId from the path
      // Now the [Done Editing] button sets the doneEditing flag to prevent this overwrite only in case the button was actually clicked.
      if (doneEditing) {
        if (activeLayout) {
          setActiveLayout(null);
        }
      } else {
        setActiveLayout({ uuid: params?.layoutId });
      }
      setEditVersionLayout(params?.version);
      setShowVersionLayout(null);
    }
    // IMPORTANT: do not include 'doneEditing' to the dependency list, as the effect changes the value, and it would thus re-run the effect, undoing the fix for 2 click problem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.layoutId, params?.version, activeLayout, setActiveLayout, setEditVersionLayout, setShowVersionLayout, setDoneEditing]);

  useEffect(() => {
    if (
      !params?.galleryId &&
      showVersionLayout &&
      location.pathname === "/main"
    ) {
      setShowVersionLayout(null);
    }
  }, [params?.galleryId, location.pathname, setShowVersionLayout, showVersionLayout]);

  // Clear editor state when navigating away from editor/gallery routes to dashboard
  useEffect(() => {
    const isEditorRoute = params?.layoutId || params?.galleryId;
    if (!isEditorRoute && (activeLayout || showVersionLayout)) {
      setActiveLayout(null);
      setShowVersionLayout(null);
      setEditVersionLayout(null);
    }
  }, [location.pathname, params?.layoutId, params?.galleryId, activeLayout, showVersionLayout, setActiveLayout, setShowVersionLayout, setEditVersionLayout]);

  useEffect(() => {
    if (params?.galleryId && !showVersionLayout && !activeLayout) {
      setActiveLayout(null);
      setEditVersionLayout(null);
      setShowVersionLayout(params?.galleryId);
    }
  }, [params?.galleryId, setActiveLayout, setEditVersionLayout, setShowVersionLayout, showVersionLayout, activeLayout]);

  const filterLayouts = filterLayoutsFromNode;

  // =====================================================
  // FIX: Remove /main guard to prevent dashboard flash
  // =====================================================
  // Previously: if (activeLayout && location.pathname != "/main")
  // This caused a flash because setActiveLayout() runs before navigate(),
  // so activeLayout was set while path was still /main, causing
  // the dashboard to render briefly before the navigation completed.
  // Now: if activeLayout exists, always render UploadFloor regardless of path.
  // =====================================================
  
  // DIAGNOSTIC: Log which branch we're taking
  if (process.env.NODE_ENV !== 'production' || redirectToEditor) {
    console.log('[SKETCH-UPLOAD] Render decision:', {
      hasActiveLayout: !!activeLayout,
      activeLayoutUuid: activeLayout?.uuid,
      hasShowVersionLayout: !!showVersionLayout,
      pathname: location?.pathname,
      willRender: activeLayout ? 'UploadFloor' : showVersionLayout || params?.galleryId ? 'LayoutVersionSlider' : 'Dashboard',
    });
  }
  
  if (activeLayout) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('');
      console.log('╔' + '═'.repeat(70) + '╗');
      console.log('║  🚀 [SKETCH-UPLOAD] RENDERING UPLOADFLOOR (EDITOR)                    ║');
      console.log('╠' + '═'.repeat(70) + '╣');
      console.log('║  activeLayout.uuid:', activeLayout?.uuid);
      console.log('║  activeLayout.name:', activeLayout?.name);
      console.log('║  editVersionLayout:', editVersionLayout);
      console.log('║  pathname:', location?.pathname);
      console.log('╚' + '═'.repeat(70) + '╝');
    }
    return (
      <>
        <Fade in={forkTransitioning} unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "background.default",
              zIndex: 1299,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={40} />
              <Typography variant="h6" fontWeight={600}>
                Saving your layout...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This will only take a moment
              </Typography>
            </Stack>
          </Box>
        </Fade>
        <UploadFloor
          setShowVersionLayout={setShowVersionLayout}
          setProgress={setProgress}
          versionLayout={editVersionLayout}
          setVersionLayout={setEditVersionLayout}
          setSketchUploadValue={setValue}
          directoryData={layoutData}
          refetchDirectoryData={refetchLayoutsData}
          layoutId={activeLayout?.uuid}
          setActiveLayout={setActiveLayout}
          setStackedLayouts={setStackedLayouts}
        />
      </>
    );
  }
  // Same fix for showVersionLayout
  else if (redirectToEditor) {
    return (<Box/>);
  }
  else if (showVersionLayout || params?.galleryId) {
    return (
      <Box
        sx={
          location.pathname.startsWith("/admin/floor-gallery/")
            ? {
                flex: 1,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }
            : undefined
        }
      >
        <ErrorBoundary>
          <LayoutVersionSlider
            refetchDirectoryData={refetchLayoutsData}
            showVersionLayout={showVersionLayout || params?.galleryId}
            setShowVersionLayout={setShowVersionLayout}
            versionSliderData={versionSliderData}
            setActiveLayout={setActiveLayout}
          />
        </ErrorBoundary>
      </Box>
    );
  }

  return (
    <>
      {title?.length >= 1 && <DynamicAlert status={alertStatus} title={title} />}
      {successAlert && <SuccessAlert title={successAlert} />}

      <Container
        component="main"
        sx={{
          width: "100%",
          maxWidth: "100% !important",
          px: "0 !important",
          display: "flex",
          flexDirection: "column",
          ...(libraryFillsParentHeight
            ? { flex: 1, minHeight: 0, height: "100%" }
            : {}),
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            pt: bentoHomeChromeHidden ? 0 : 2,
            display: "flex",
            width: "100%",
            minHeight: 0,
            ...(libraryFillsParentHeight
              ? { flex: 1, overflow: "hidden" }
              : {}),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              position: "relative",
              width: "100%",
              height: "100%",
              minHeight: 0,
              ...(libraryFillsParentHeight ? { overflow: "hidden" } : {}),
            }}
          >
            {!bentoHomeChromeHidden && (
            <Stack
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              px={2}
            >
              {value === "4" ? null : (
                <>
                  <Stack
                    flexDirection={"row"}
                    alignItems={"center"}
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(mainHomeEmbedded ? "/main" : "/")
                    }
                  >
                    <LogoBgIcon width={40} height={40} />
                  </Stack>

                  {!currentUserSessionStorage ? (
                    <Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        bgcolor: "#EBEDF0",
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (value === "6" && !(layoutData?.length >= 1)) {
                          resetProgress();
                          setValue("3");
                        } else if (value === "6") {
                          resetProgress();
                          setValue("1");
                        } else if (value === "3") {
                          setValue("1");
                        } else {
                          setValue("3");
                          setShowVersionLayout(null);
                          navigate("/main");
                        }
                      }}
                    >
                      {value !== "3" && value !== "5" && <UserIcon />}
                      <Typography fontSize={"13px"} fontWeight={"600"}>
                        {value === "3" || value === "5"
                          ? "Back to home"
                          : "My Account"}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Stack>
            )}

            {value !== "4" && !bentoHomeChromeHidden && (
              <Divider sx={{ borderColor: "#DADADA", my: 1 }} />
            )}

            {currentUserSessionStorage ? (
              <>
                {value !== "3" &&
                  value !== "4" &&
                  value !== "5" &&
                  value !== "6" && (
                    <>
                      {!(bentoHomeChromeHidden && combineMainBentoLibrary) && (
                      <Box
                        sx={{
                          px:
                            bentoHomeChromeHidden && combineMainBentoLibrary
                              ? { xs: 1.25, sm: 2 }
                              : bentoHomeChromeHidden
                                ? { xs: 1.5, sm: 2 }
                                : 2,
                          pt: bentoHomeChromeHidden ? 1.5 : 0,
                        }}
                      >
                        {bentoHomeChromeHidden && (
                          <>
                            <Typography
                              component="h2"
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                fontSize: { xs: "0.95rem", sm: "1rem" },
                                lineHeight: 1.3,
                                color: "text.primary",
                                width: "100%",
                                textAlign: combineMainBentoLibrary
                                  ? { xs: "center", md: "left" }
                                  : "inherit",
                              }}
                            >
                              {combineMainBentoLibrary
                                ? "Folders and projects"
                                : "Floor plans"}
                            </Typography>
                            {!combineMainBentoLibrary && (
                            <Typography
                              sx={{
                                fontSize: "0.8125rem",
                                lineHeight: 1.4,
                                color: "text.secondary",
                                mt: 0.25,
                                mb: 1.5,
                              }}
                            >
                              Open a saved plan or organize folders. New uploads from above appear here when saved to your account.
                            </Typography>
                            )}
                          </>
                        )}
                        {!combineMainBentoLibrary && (
                        <Box
                          component={bentoHomeChromeHidden ? Paper : Box}
                          variant={bentoHomeChromeHidden ? "outlined" : undefined}
                          sx={
                            bentoHomeChromeHidden
                              ? {
                                  borderRadius: 2,
                                  borderColor: "rgba(0,0,0,0.08)",
                                  p: "4px",
                                  bgcolor: "#F2F2F7",
                                  maxWidth: "100%",
                                  width: "100%",
                                  mx: 0,
                                }
                              : {
                                  backgroundColor: "#EBEDF0",
                                  borderRadius: "9px",
                                  padding: "2px",
                                  maxWidth: "sm",
                                  width: "100%",
                                  mx: "auto",
                                }
                          }
                        >
                          <TabContext value={value}>
                            <TabList
                              value={value}
                              TabIndicatorProps={{
                                style: {
                                  color: "transparent",
                                  backgroundColor: "transparent",
                                },
                              }}
                              onChange={handleChange}
                              aria-label="lab API tabs example"
                              sx={{ maxHeight: "32px", minHeight: "32px" }}
                            >
                              <Tab
                                label="Floor Plans"
                                value="1"
                                sx={{
                                  p: 0,
                                  minWidth: "fit-content",
                                  width: "fit-content",
                                  minHeight: "41px",
                                  color:
                                    value === "1" ? "black !important" : "",
                                  backgroundColor:
                                    value === "1" ? "white !important" : "",
                                  borderRadius: "7px !important",
                                  flex: 1,
                                  height: "32px",
                                }}
                              />

                              <Tab
                                label="Folders"
                                value="2"
                                sx={{
                                  p: 0,
                                  minWidth: "fit-content",
                                  width: "fit-content",
                                  minHeight: "41px",
                                  backgroundColor:
                                    value === "2" ? "white !important" : "",
                                  borderRadius: "7px !important",
                                  color:
                                    value === "2" ? "black !important" : "",
                                  flex: 1,
                                  height: "32px",
                                }}
                              />
                            </TabList>
                          </TabContext>
                        </Box>
                        )}
                      </Box>
                      )}
                      {!combineMainBentoLibrary && (
                      <Divider sx={{ borderColor: "#DADADA", mt: 1 }} />
                      )}
                    </>
                  )}

                <Container
                  maxWidth={
                    value === "3" ||
                    value === "4" ||
                    value === "5"
                      ? "xs"
                      : value === "6"
                      ? ""
                      : bentoHomeChromeHidden
                      ? false
                      : "md"
                  }
                  sx={
                    libraryFillsParentHeight
                      ? {
                          // /main bento (combine + chrome hidden): cards fill the Paper directly,
                          // no padding (Paper is already sized to match the upload card column).
                          px:
                            combineMainBentoLibrary && bentoHomeChromeHidden
                              ? 0
                              : 2,
                          mx: "auto",
                          width: "100%",
                          maxWidth: "100% !important",
                          flex: 1,
                          minHeight: 0,
                          maxHeight: "100%",
                          overflowY: "auto",
                          display: combineMainBentoLibrary ? "flex" : "block",
                          flexDirection: combineMainBentoLibrary
                            ? "column"
                            : undefined,
                          justifyContent: "flex-start",
                          alignItems: "stretch",
                          "&::-webkit-scrollbar": {
                            display: "none",
                          },
                          scrollbarWidth: "none",
                        }
                      : {
                          // /main bento (combine + chrome hidden): cards fill the Paper directly,
                          // no padding (Paper is already sized to match the upload card column).
                          px:
                            combineMainBentoLibrary && bentoHomeChromeHidden
                              ? 0
                              : 2,
                          mx: "auto",
                          width: "100%",
                          maxWidth:
                            value === "6"
                              ? "550px !important"
                              : bentoHomeChromeHidden
                              ? "100% !important"
                              : "xs",
                          ...(combineMainBentoLibrary && bentoHomeChromeHidden
                            ? {
                                minHeight: "auto",
                                maxHeight: "none",
                                overflowY: "visible",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                alignItems: "stretch",
                              }
                            : {
                                maxHeight:
                                  value === "3" || value === "5" || value === "6"
                                    ? "calc(100dvh - 83px)"
                                    : value === "4"
                                    ? "calc(100dvh - 26px)"
                                    : `calc(100dvh - ${
                                        stackedLayouts?.length === 0 &&
                                        value === "2"
                                          ? 243
                                          : stackedLayouts?.length === 0
                                          ? 186.5
                                          : 186.5 + stackedLayouts.length * 56
                                      }px)`,

                                minHeight:
                                  value === "3" || value === "5" || value === "6"
                                    ? "calc(100dvh - 83px)"
                                    : value === "4"
                                    ? "calc(100dvh - 26px)"
                                    : `calc(100dvh - ${
                                        stackedLayouts?.length === 0 &&
                                        value === "2"
                                          ? 243
                                          : stackedLayouts?.length === 0
                                          ? 186.5
                                          : 186.5 + stackedLayouts.length * 56
                                      }px)`,
                                overflowY: "auto",
                                display:
                                  value === "3" ||
                                  value === "4" ||
                                  value === "5" ||
                                  value === "6"
                                    ? "flex"
                                    : "block",
                                justifyContent: "center",
                                alignItems: "center",
                              }),
                          "&::-webkit-scrollbar": {
                            display: "none",
                          },
                          scrollbarWidth: "none",
                        }
                  }
                >
                  {combineMainBentoLibrary ? (
                    <MainBentoCombinedLibraryBody
                      layoutsFetching={layoutsFetching}
                      libraryFillsParentHeight={libraryFillsParentHeight}
                      floorPlanItems={bentoFloorPlanItems ?? []}
                      sectionTitle={bentoFloorPlanSectionTitle}
                      navigate={navigate}
                      setActiveLayout={setActiveLayout}
                      setShowVersionLayout={setShowVersionLayout}
                      setEditVersionLayout={setEditVersionLayout}
                      clickTimeout={clickTimeout}
                      setClickTimeout={setClickTimeout}
                      setOptionsMenu={setOptionsMenu}
                      moreOptionsMenu={moreOptionsMenu}
                      setEditableLayoutId={setEditableLayoutId}
                      editableLayoutId={editableLayoutId}
                      bentoDraggingPlanId={bentoDraggingPlanId}
                      onBentoPlanDragStart={onBentoPlanDragStart}
                      onBentoPlanDragEnd={onBentoPlanDragEnd}
                    />
                  ) : value === "1" ? (
                    <>
                      {layoutsFetching ? (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            ...(libraryFillsParentHeight
                              ? {
                                  flex: 1,
                                  minHeight: 200,
                                  maxHeight: "100%",
                                }
                              : {
                                  maxHeight: "calc(100dvh - 186.5px)",
                                  minHeight: "calc(100dvh - 186.5px)",
                                }),
                          }}
                        >
                          <LoaderSpinner text={false} />
                        </Box>
                      ) : layouts?.length >= 1 ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <Grid container spacing={2} mt={0}>
                            {layoutData
                              ?.flatMap(filterLayouts)
                              .filter((item) => item.image)?.length >= 1 ? (
                              layoutData
                                ?.flatMap(filterLayouts)
                                ?.filter(
                                  (item) =>
                                    item.image &&
                                    item?.userSavedLayoutsCount > 0
                                )
                                ?.slice()
                                ?.reverse()
                                ?.map((item, idx) => {
                                  
                                  const cachedData = layoutCacheStore.get(String(item?._id));
                                  let cachedImage = cachedData?.versions?.[item?.version];
                                   getBase64Image(cachedImage?.url).then((isValid) => {
                                      if (!isValid) {
                                        // Only null the local variable — do NOT write null back
                                        // to layoutCacheStore. The editor's runBackToGallery writes
                                        // a fresh blob there immediately before navigate(), and the
                                        // gallery's fetchCache effect reads it on mount. Nulling the
                                        // shared store here from the home-grid thumbnail validation
                                        // fires-and-forgets async, races against that gallery mount,
                                        // and was the root cause of the stale-gallery-image bug:
                                        // validation incorrectly returned null (HEAD on blob: URLs
                                        // always fails in Chromium) → store wiped → gallery fell
                                        // back to the stale server CDN URL.
                                        cachedImage = null;
                                      }
                                    }).catch((error) => {
                                      console.error("Error validating cached image:", error);
                                    });
                                
                                  
                                  return (
                                    <Grid key={idx} item xs={6} sm={4}>
                                      <Box
                                        sx={{
                                          border: "1px solid #EBEDF0",
                                          py: 2,
                                          px: 1.5,
                                          borderRadius: "8px",
                                          position: "relative",
                                          background: "#EBEDF080",
                                        }}
                                        onDoubleClick={() => {
                                          if (clickTimeout)
                                            clearTimeout(clickTimeout);
                                          setActiveLayout({
                                            name: item?.name,
                                            uuid: item?.uuid,
                                          });
                                        }}
                                        onClick={() => {
                                          if (clickTimeout)
                                            clearTimeout(clickTimeout);
                                          const timeout = setTimeout(() => {
                                            if (
                                              item?.version == 2 &&
                                              item?.payload?.roomsDataCount == 0
                                            ) {
                                              setActiveLayout(item);
                                              setEditVersionLayout(2);
                                              navigate(`/floor-plan/${item?.uuid}/version/${2}`);
                                            } else {
                                              setShowVersionLayout(item?._id);
                                              navigate(`/floor-gallery/${item?._id}`);
                                            }
                                          }, 200);
                                          setClickTimeout(timeout);
                                        }}
                                      >
                                        <Box
                                          color="primary"
                                          sx={{
                                            position: "absolute",
                                            top: 8,
                                            left: 8,
                                            zIndex: 999,
                                            color: "white",
                                            padding: "3px 6px 3px 6px",
                                            backgroundColor: "#20212466",
                                            borderRadius: "4px !important",
                                            fontWeight: 600,
                                          }}
                                        >
                                        {Math.max(item?.userSavedLayoutsCount - 1, 0)} {" "}
                                          Layout
                                        </Box>

                                        <IconButton
                                          color="primary"
                                          sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            zIndex: 999,
                                            backgroundColor: "white",
                                            borderRadius: "50% !important",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setOptionsMenu(moreOptionsMenu);
                                            setEditableLayoutId(item);
                                          }}
                                        >
                                          <MoreVertOutlinedIcon
                                            sx={{
                                              fontSize: 14,
                                              fontWeight: "bold",
                                            }}
                                          />
                                        </IconButton>

                                        <Avatar
                                          src={
                                            cachedImage?.url ||
                                            item?.thumbnailFurnishedImage ||
                                            item?.thumbnailImage ||
                                            item?.furnishedImageId ||
                                            item?.image
                                          }
                                          alt={item?.name || "Avatar"} 
                                          sx={{
                                            width: "100%",
                                            "& img": {
                                              width: "100%",
                                              objectFit: "contain ",
                                            },
                                            height: 115,
                                            borderRadius: 0,
                                          }}
                                        />
                                      </Box>

                                      <Typography
                                        sx={{
                                          color: "#545454",
                                          fontSize: "14px",
                                          mt: 0.5,
                                          fontWeight: 800,
                                        }}
                                      >
                                        {item?.name ||
                                          truncateText(
                                            item?.payload?.prompt,
                                            19
                                          ) ||
                                          "Untitled"}
                                      </Typography>
                                    </Grid>
                                  );
                                })
                            ) : (
                              <Grid item xs={12}>
                                <Typography>
                                  No floorplans found. Please create a new
                                  floorplan to get started!
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <Grid container spacing={2} mt={0}>
                            <Grid item xs={12}>
                              No floorplans found. Please create a new floorplan
                              to get started!
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </>
                  ) : value === "2" ? (
                    <>
                      {layoutsFetching ? (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            ...(libraryFillsParentHeight
                              ? {
                                  flex: 1,
                                  minHeight: 200,
                                  maxHeight: "100%",
                                }
                              : {
                                  maxHeight: "calc(100dvh - 186.5px)",
                                  minHeight: "calc(100dvh - 186.5px)",
                                }),
                          }}
                        >
                          <LoaderSpinner text={false} />
                        </Box>
                      ) : layoutData?.filter((item) => !item.image)?.length >=
                        1 ? (
                        <Box sx={{ width: "100%", height: "100%" }}>
                          <Grid container spacing={2} mt={0}>
                            {showLayouts?.length > 1 && (
                              <Grid item xs={12}>
                                <Typography
                                  color={(theme) =>
                                    theme.palette.customPrimary.main
                                  }
                                  fontSize={"14px"}
                                  fontWeight={500}
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => {
                                    const newArr = [...showLayouts];
                                    newArr.pop();
                                    setShowLayouts(newArr);

                                    const newArrParent = [...parentLayout];
                                    newArrParent.pop();
                                    setParentLayout(newArrParent);
                                  }}
                                >
                                  {showLayouts?.length > 1 && "Back"}
                                </Typography>
                              </Grid>
                            )}

                            {showLayouts[showLayouts.length - 1]?.length >=
                            1 ? (
                              showLayouts[showLayouts.length - 1]
                                ?.filter((item) => !item.payload || item.parent)
                                ?.slice()
                                ?.reverse()
                                ?.map((item, idx) => {
                                  return (
                                    <Grid
                                      item
                                      key={`Box-${item?._id}-${idx}`}
                                      xs={6}
                                      sm={4}
                                    >
                                      <Box
                                        sx={{
                                          border: "1px solid #EBEDF0",
                                          py: 2,
                                          px: 1.5,
                                          borderRadius: "8px",
                                          position: "relative",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => {
                                          if (item?.payload) {
                                            if (clickTimeout)
                                              clearTimeout(clickTimeout);
                                            const timeout = setTimeout(() => {
                                              setShowVersionLayout(item?._id);
                                              navigate(
                                                `/floor-gallery/${item?._id}`
                                              );
                                            }, 200);
                                            setClickTimeout(timeout);
                                          } else {
                                            setShowLayouts([
                                              ...showLayouts,
                                              item.children || [],
                                            ]);
                                            setParentLayout([
                                              ...parentLayout,
                                              item?._id,
                                            ]);
                                            setEditableLayoutId(null);
                                          }
                                        }}
                                        onDoubleClick={() => {
                                          if (item?.payload) {
                                            if (clickTimeout)
                                              clearTimeout(clickTimeout);
                                            setActiveLayout({
                                              name: item?.name,
                                              uuid: item?.uuid,
                                            });
                                            navigate(
                                              `/floor-plan/${item?.uuid}/version/${item?.version}`
                                            );
                                          }
                                        }}
                                      >
                                        <IconButton
                                          color="primary"
                                          sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            zIndex: 999,
                                            backgroundColor: "white",
                                            borderRadius: "50% !important",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setOptionsMenu(moreOptionsMenu);
                                            setEditableLayoutId(item);
                                          }}
                                        >
                                          <MoreVertOutlinedIcon
                                            sx={{
                                              fontSize: 14,
                                              fontWeight: "bold",
                                            }}
                                          />
                                        </IconButton>

                                        {item?.payload && (
                                          <Box
                                            color="primary"
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              left: 8,
                                              zIndex: 999,
                                              color: "white",
                                              padding: "3px 6px 3px 6px",
                                              backgroundColor: "#20212466",
                                              borderRadius: "4px !important",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {item?.userSavedLayoutsCount || 1}{" "}
                                            Layout
                                          </Box>
                                        )

                                        }

                                        <Avatar
                                          src={
                                            item?.payload
                                              ? item?.furnishedImageId ||
                                                item?.image
                                              : "https://www.thefinalstep.co.uk/hs-fs/hubfs/Screenshot%202022-05-04%20at%2015.04.03.png?width=986&name=Screenshot%202022-05-04%20at%2015.04.03.png"
                                          }
                                          alt={item?.name || "Avatar"}
                                          sx={{
                                            width: "100%",
                                            "& img": {
                                              width: "100%",
                                              objectFit: "contain ",
                                            },
                                            height: 115,
                                            borderRadius: 0,
                                          }}
                                        />

                                        {editableLayoutId == item?._id && (
                                          <Box
                                            sx={{
                                              border: "0.5px solid #C1C1C1",
                                              borderRadius: "50px",
                                              display: "flex",
                                              flexDirection: "row",
                                              gap: 1,
                                              bottom: "10px",
                                              background: "white",
                                              zIndex: 99999,
                                              position: "absolute",
                                              justifyContent: "center",
                                              width: "fit-content",
                                              padding: "8px 15px",
                                            }}
                                          >
                                            <IconButton
                                              color="primary"
                                              sx={{ p: 0 }}
                                              onClick={(e) => {
                                                e.stopPropagation();

                                                setSelectedFolder(item);
                                                setShowCopyFolder(true);
                                              }}
                                            >
                                              <ContentCopyOutlinedIcon />
                                            </IconButton>

                                            <Box
                                              sx={{
                                                borderLeft:
                                                  "0.5px solid #C1C1C1",
                                              }}
                                            ></Box>

                                            <IconButton
                                              color="primary"
                                              onMouseDown={(e) =>
                                                e.stopPropagation()
                                              }
                                              onTouchStart={(e) =>
                                                e.stopPropagation()
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();

                                                setSelectedFolder(item);
                                                setShowMoveFolder(true);
                                              }}
                                              sx={{ p: 0 }}
                                            >
                                              <DriveFileMoveOutlinedIcon />
                                            </IconButton>

                                            <Box
                                              sx={{
                                                borderLeft:
                                                  "0.5px solid #C1C1C1",
                                              }}
                                            ></Box>

                                            <IconButton
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setItemToDelete(item);
                                              }}
                                              onMouseDown={(e) =>
                                                e.stopPropagation()
                                              }
                                              onTouchStart={(e) =>
                                                e.stopPropagation()
                                              }
                                              sx={{ p: 0 }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        )}
                                      </Box>

                                      <Typography
                                        sx={{
                                          color: "#545454",
                                          fontSize: "14px",
                                          mt: 0.5,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {item?.name ||
                                          truncateText(
                                            item?.payload?.prompt,
                                            19
                                          ) ||
                                          "Untitled"}
                                      </Typography>
                                    </Grid>
                                  );
                                })
                            ) : (
                              <Grid item xs={12}>
                                <Typography>
                                  No folders found. Organize your work by
                                  creating a new folder!
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <Grid container spacing={2} mt={0}>
                            <Grid item xs={12}>
                              No folders found. Organize your work by creating a
                              new folder!
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </>
                  ) : value === "3" ? (
                    <Box
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Stack
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        <Stack
                          flexDirection={"row"}
                          gap={2}
                          alignItems={"center"}
                        >
                          <Typography className="big-header">
                            My Account
                          </Typography>
                        </Stack>

                        <Typography
                          sx={{
                            color: "#FF5151",
                            fontSize: "14px",
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                          onClick={LogoutMain}
                        >
                          Log Out
                        </Typography>
                      </Stack>

                      <Stack gap={2} mt={2}>
                        {profile?.name && (
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={2}
                          >
                            <Typography
                              minWidth={"100px"}
                              className="black"
                              sx={{ fontWeight: 500, fontSize: "14px" }}
                            >
                              Name
                            </Typography>
                            <Typography
                              className="black"
                              sx={{ fontWeight: 400, fontSize: "14px" }}
                            >
                              {profile?.name}
                            </Typography>
                          </Stack>
                        )}

                        <Stack
                          flexDirection={"row"}
                          alignItems={"center"}
                          gap={2}
                        >
                          <Typography
                            minWidth={"100px"}
                            className="black"
                            sx={{ fontWeight: 500, fontSize: "14px" }}
                          >
                            Email
                          </Typography>
                          <Typography
                            className="black"
                            sx={{ fontWeight: 400, fontSize: "14px" }}
                          >
                            {profile?.email}
                          </Typography>
                        </Stack>

                        <Stack
                          flexDirection={"row"}
                          alignItems={"center"}
                          gap={2}
                        >
                          <Typography
                            minWidth={"100px"}
                            className="black"
                            sx={{ fontWeight: 500, fontSize: "14px" }}
                          >
                            Password
                          </Typography>
                          <Typography
                            className="black"
                            sx={{ fontWeight: 400, fontSize: "14px" }}
                          >
                            {"*".repeat(profile?.password || "8")}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack
                        flexDirection={"row"}
                        justifyContent={"flex-end"}
                        mt={2}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: (theme) => theme.palette.customPrimary.main,
                            cursor: "pointer",
                          }}
                          onClick={() => setValue("4")}
                        >
                          Change Password
                        </Typography>
                      </Stack>

                      <Stack mt={6}>
                        <DragAndDropFile logo={profile?.logo} />
                      </Stack>

                      <Stack mt={3}>
                        <Typography
                          fontWeight={500}
                          className="black"
                          fontSize={"14px"}
                        >
                          Disclaimer on exported floor plans
                        </Typography>

                        <Box
                          sx={{
                            mt: 0.5,
                            px: 2,
                            bgcolor: "#FAFAFAED",
                            border: "1px solid #54545466",
                            borderRadius: "8px",
                            minHeight: "41px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {truncateText(profile?.disclaimer, 61) ||
                            "No Disclaimer"}
                        </Box>

                        <Box mt={3}>
                          <LoadingButton
                            className="button-style"
                            variant="contained"
                            fullWidth
                            onClick={() => setValue("5")}
                            sx={mfModalPillButtonSx}
                          >
                            Edit
                          </LoadingButton>
                        </Box>
                      </Stack>
                    </Box>
                  ) : value === "4" ? null : value === "5" ? (
                    <Box
                      sx={{
                        width: "100%",
                      }}
                    >
                      <UpdateProfile setValue={setValue} />
                    </Box>
                  ) : value === "6" ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                        justifyContent: "center",
                      }}
                    >
                      
                      {errorProcessImageRef.current && (
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(255, 81, 81, 1)" , textAlign:"center" }}
                        >
                          Unable to process the image. Please try again with a
                          different one.
                        </Typography>
                      )}

                      <NewUpload
                        errorProcessImageRef={errorProcessImageRef}
                        setVersionLayout={setEditVersionLayout}
                        checkLength={
                          errorProcessImageRef.current ? false : true
                        }
                        projectDetails={projectDetails}
                        setProjectDetails={setProjectDetails}
                        click={fileInputRef}
                        setValue={setValue}
                        progress={progress}
                        uploadImage={uploadImage}
                        payloadData={payloadData}
                        setProgress={setProgress}
                        setUploadImage={setUploadImage}
                        onCancel={() => {
                          handleCancelRequest();
                          setUploadImage(null);
                          resetProgress();

                          setLoading(false);
                          setValue("1");
                        }}
                        setActiveLayout={setActiveLayout}
                      />
                    </Box>
                  ) : null}
                </Container>
              </>
            ) : (

              <Container
                  sx={{
                  px: 2,
                  height: "calc(100dvh - 89px)", 
                  maxWidth:"550px !important",
                  overflow:"auto"
                  }}
                  >
              <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: uploadImage ? 5 :"0px",
                justifyContent: "center",
              }}
            >
            {!uploadImage && (
              <>
                  <Typography fontWeight={900} fontSize={"24px"} textAlign={"center"}>
                    See Your Plan Furnished
                  </Typography>
        
                  <Typography variant="body2" mt={0.5} mb={errorProcessImageRef.current  ?0: 2}  fontSize={"16px"}  textAlign={"center"}>
                    Upload a floor plan to get started
                  </Typography>
              </>
                )}

                  {errorProcessImageRef.current && (
                        <Typography
                        mt={0.5}
                        mb={2}
                        variant="body2"
                        sx={{ color: "rgba(255, 81, 81, 1)", textAlign:"center" }}
                        >
                          Sorry, something went wrong. Please try again. 
                        </Typography>
                      )}


                  <NewUpload
                        errorProcessImageRef={errorProcessImageRef}
                        setVersionLayout={setEditVersionLayout}
                        checkLength={
                          errorProcessImageRef.current ? false : true
                        }
                        projectDetails={projectDetails}
                        setProjectDetails={setProjectDetails}
                        click={fileInputRef}
                        setValue={setValue}
                        progress={progress}
                        uploadImage={uploadImage}
                        payloadData={payloadData}
                        setProgress={setProgress}
                        setUploadImage={setUploadImage}
                        disclaimer={true}
                        onCancel={() => {
                          handleCancelRequest();
                          setUploadImage(null);
                          resetProgress();

                          setLoading(false);
                        }}
                        setActiveLayout={setActiveLayout}
                      />
              </Box>
              </Container>
            )}

            {stackedLayouts.length > 0 &&
              !showVersionLayout &&
              value !== "3" &&
              value !== "4" &&
              value !== "5" &&
              value !== "6" &&
              stackedLayouts
                ?.slice()
                ?.reverse()
                ?.map((layout, index) => {
                  return (
                    <Box
                      maxWidth={"sm"}
                      width={"100%"}
                      key={layout?.id || index}
                      sx={{
                        mx: "auto",
                        px: 2,
                        position: "relative", 
                        marginTop: index > 0 ? "0px" : "0px", 
                        zIndex: stackedLayouts.length - index, 
                      }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 16px",
                          borderRadius: "12px",
                          width: "100%",
                          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                          cursor: "pointer",
                          pl: 2,
                        }}
                        onClick={() => {
                          setEditVersionLayout(layout?.version || null);
                          setActiveLayout(layout);
                          navigate(
                            `/floor-plan/${layout?.uuid}/version/${layout?.version}`
                          );
                        }}
                      >
                        <IconButton size="small" sx={{ pl: 0 }}>
                          <ExpandLessIcon />
                        </IconButton>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {layout?.name || "Untitled"}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStackedLayouts([
                              ...stackedLayouts.filter(
                                (stackedLayout) =>
                                  stackedLayout._id !== layout?._id
                              ),
                            ]);
                          }}
                        >
                          <ExitIcon />
                        </IconButton>
                      </Paper>
                    </Box>
                  );
                })}

            {value !== "3" &&
              value !== "4" &&
              value !== "5" &&
              value !== "6" &&
              !showVersionLayout &&
              !editableLayoutId?._id &&
              currentUserSessionStorage && (
                <Box
                  id="legacy-inline-upload-floor-plan-actions"
                  aria-hidden={
                    !!(mainHomeEmbedded && (value === "1" || value === "2"))
                  }
                  width={"100%"}
                  pt={"10px"}
                  gap={1}
                  maxWidth={"sm"}
                  mx={"auto"}
                  px={2}
                  display={
                    mainHomeEmbedded && (value === "1" || value === "2")
                      ? "none"
                      : "flex"
                  }
                  flexDirection={"column"}
                >
                  <LoadingButton
                    variant="contained"
                    className="button-style"
                    fullWidth
                    startIcon={<ShareWhiteIcon />}
                    onClick={() => {
                      setProgress(0);
                      fileInputRef.current.click();
                    }}
                    loading={loading}
                    sx={mfModalPillButtonSx}
                  >
                    {"Upload new floor plan"}
                  </LoadingButton>

                  {value === "2" && (
                    <LoadingButton
                      variant="contained"
                      className="button-style cancel-button"
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setShowNewFolder(true);
                      }}
                      loading={loading}
                      sx={mfModalPillButtonSx}
                    >
                      {"Add new folder"}
                    </LoadingButton>
                  )}
                </Box>
              )}
          </Box>
        </Box>

        {editableLayoutId && optionsMenu && (
          mainHomeEmbedded ? (
            <ErrorBoundary>
              <LayoutOptionMenu
                key={editableLayoutId?._id}
                presentation="dialog"
                layoutData={editableLayoutId}
                onClose={() => setEditableLayoutId(null)}
                optionsMenu={optionsMenu}
                refetch={refetchLayoutsData}
              />
            </ErrorBoundary>
          ) : (
            <Container
              maxWidth={"sm"}
              sx={{
                px: 0,
                zIndex: "999",
                position: "absolute",
                bottom: 0,
                width: "100%",
                padding: "15px",
                left: "50%",
                transform: "translateX(-50%)",
              }}
              className={`page-footer ${editableLayoutId?._id ? "menu" : ""}`}
            >
              <ErrorBoundary>
                <LayoutOptionMenu
                  key={editableLayoutId?._id}
                  layoutData={editableLayoutId}
                  onClose={() => setEditableLayoutId(null)}
                  optionsMenu={optionsMenu}
                  refetch={refetchLayoutsData}
                />
              </ErrorBoundary>
            </Container>
          )
        )}

        <input
          id="upload-button"
          type="file"
          style={{ display: "none" }}
          accept=".png, .jpeg, .jpg, .pdf"
          ref={fileInputRef}
          disabled={loading}
          onChange={handleFileChange}
        />

        <StandardDialog
          open={value === "4"}
          onClose={() => setValue("3")}
          title="Change your password"
          footerLayout="center"
          PaperProps={{ sx: { overflow: "visible" } }}
          footer={
            <LoadingButton
              className="button-style"
              loading={load}
              variant="contained"
              fullWidth
              size="large"
              onClick={formik.handleSubmit}
              sx={mfModalPillButtonSx}
            >
              Change password
            </LoadingButton>
          }
        >
          <Stack gap={2}>
            <TextField
              required
              fullWidth
              name="oldPassword"
              placeholder="Old Password"
              type={showPsw ? "text" : "password"}
              id="old-password"
              onChange={formik.handleChange}
              error={
                formik.touched.oldPassword && Boolean(formik.errors.oldPassword)
              }
              helperText={
                formik.touched.oldPassword && formik.errors.oldPassword
              }
              onBlur={formik.handleBlur}
              className={
                formik.touched.oldPassword && formik.errors.oldPassword
                  ? null
                  : "white"
              }
              sx={changePasswordFieldSx}
              InputProps={{
                style: { minHeight: 48 },
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => setShowPsw(!showPsw)}
                    sx={{ cursor: "pointer" }}
                  >
                    {showPsw ? (
                      <Typography className="input-text">Hide</Typography>
                    ) : (
                      <Typography className="input-text">Show</Typography>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              required
              fullWidth
              name="password"
              placeholder="New Password"
              type={showPsw ? "text" : "password"}
              id="new-password"
              onChange={formik.handleChange}
              error={
                formik.touched.password && Boolean(formik.errors.password)
              }
              helperText={formik.touched.password && formik.errors.password}
              onBlur={formik.handleBlur}
              className={
                formik.touched.password && formik.errors.password
                  ? null
                  : "white"
              }
              sx={changePasswordFieldSx}
              InputProps={{
                style: { minHeight: 48 },
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => setShowPsw(!showPsw)}
                    sx={{ cursor: "pointer" }}
                  >
                    {showPsw ? (
                      <Typography className="input-text">Hide</Typography>
                    ) : (
                      <Typography className="input-text">Show</Typography>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              required
              fullWidth
              name="confirmPassword"
              placeholder="Confirm Password"
              type={showPsw ? "text" : "password"}
              id="confirm-password"
              onChange={formik.handleChange}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword &&
                formik.errors.confirmPassword
              }
              onBlur={formik.handleBlur}
              className={
                formik.touched.confirmPassword &&
                formik.errors.confirmPassword
                  ? null
                  : "white"
              }
              sx={changePasswordFieldSx}
              InputProps={{
                style: { minHeight: 48 },
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => setShowPsw(!showPsw)}
                    sx={{ cursor: "pointer" }}
                  >
                    {showPsw ? (
                      <Typography className="input-text">Hide</Typography>
                    ) : (
                      <Typography className="input-text">Show</Typography>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </StandardDialog>

        <NewFolderDialog
          isOpen={showNewFolder}
          setReloadFiles={refetchLayoutsData}
          onClose={() => setShowNewFolder(false)}
          parent={
            combineMainBentoLibrary
              ? null
              : parentLayout[parentLayout.length - 1]
          }
        />

        <MoveFolderDilogue
          isOpen={showMoveFolder || showCopyFolder}
          setReloadFiles={refetchLayoutsData}
          onClose={() => {
            setShowMoveFolder(false);
            setShowCopyFolder(false);
          }}
          folders={layoutData || []}
          itemToMove={selectedFolder}
          isCopyMode={showCopyFolder}
        />

        <DeleteDialog
          isOpen={!!itemToDelete}
          onAccept={async () => {
            const id = itemToDelete?._id;
            if (!id) return;
            try {
              await deleteLayout(id).unwrap();
              layoutCacheStore.remove(String(id));
              setSuccessAlert("Deleted successfully");
              setItemToDelete(null);
              setEditableLayoutId(null);
              setProgress(0);
              await refetchLayoutsData();
              setTimeout(() => setSuccessAlert(""), 2000);
            } catch (e) {
              console.error("Delete layout failed:", e);
            }
          }}
          instruction={"Are you sure you want to delete?"}
          acceptedText="Yes, delete"
          onCancel={() => setItemToDelete(null)}
        />
      </Container>
    </>
  );
};

SketchUpload.propTypes = {
  mainHomeEmbedded: PropTypes.bool,
  /** `"bento"` = /main dashboard shell (hides duplicate chrome, restyles tabs). */
  dashboardShell: PropTypes.string,
  mainHomeBentoFill: PropTypes.bool,
  /** `null` = all plans (see `UserMainHome`). */
  selectedFolderId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  setSelectedFolderId: PropTypes.func,
  showNewFolder: PropTypes.bool,
  setShowNewFolder: PropTypes.func,
  bentoDraggingPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  onBentoPlanDragStart: PropTypes.func,
  onBentoPlanDragEnd: PropTypes.func,
  externalOptionsMenuHandle: PropTypes.shape({
    current: PropTypes.any,
  }),
};

SketchUpload.defaultProps = {
  mainHomeEmbedded: false,
  dashboardShell: null,
  mainHomeBentoFill: false,
  selectedFolderId: undefined,
  setSelectedFolderId: undefined,
  showNewFolder: undefined,
  setShowNewFolder: undefined,
  bentoDraggingPlanId: null,
  onBentoPlanDragStart: undefined,
  onBentoPlanDragEnd: undefined,
  externalOptionsMenuHandle: undefined,
};

export default SketchUpload;
